import { buildProductUpdateBody } from './product-body';
import type { ProductFormValues } from './schemas';
import type { Product } from './types';

import { apiRequest } from '@/lib/api-client';

export interface ProductRequestContext {
  readonly accessToken: string | null;
  readonly shopId: string | null;
}

/**
 * Saves a product edit WITHOUT erasing what the form does not show.
 *
 * READ-MODIFY-WRITE: the product is read from the API immediately before
 * the write, and `buildProductUpdateBody` lays the form's values over it
 * (see `product-body.ts` for why). Reading at save time, not at page-load
 * time, keeps the window in which someone else's change could be
 * overwritten down to the round trip itself rather than however long the
 * form sat open.
 *
 * If the read fails, NOTHING is written: the error surfaces exactly as a
 * failed save always did, and the stored product is untouched. Writing
 * blind — falling back to a body built from the form alone — is precisely
 * the behaviour that lost data, so there is deliberately no such fallback.
 *
 * Same two endpoints, same permission (`catalog:write`), same tenant
 * scoping as before: this adds a GET on the route the page already reads.
 */
export async function updateProduct(
  productId: string,
  values: ProductFormValues,
  { accessToken, shopId }: ProductRequestContext,
): Promise<Product> {
  const current = await apiRequest<Product>(`/merchant/products/${productId}`, {
    accessToken,
    shopId,
  });

  return apiRequest<Product>(`/merchant/products/${productId}`, {
    method: 'PUT',
    body: buildProductUpdateBody(current, values),
    accessToken,
    shopId,
  });
}
